import { useState } from "react";

const BASE = import.meta.env.VITE_API_URL ?? "";

const SIZES = ["1-10", "11-50", "51-200", "200+"] as const;

/**
 * Demo request from the public site. Plain fetch, not lib/api: there is no
 * session here, and lib/api's 401 handling would bounce a prospect to a login
 * screen they have no account for.
 */
export default function EnquiryForm({ source }: { source?: string }) {
  const [form, setForm] = useState({
    name: "", email: "", organisation: "", phone: "",
    participantCount: "" as "" | (typeof SIZES)[number],
    message: "",
    website: "", // honeypot
  });
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setForm({ ...form, [field]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setState("sending");
    try {
      const res = await fetch(`${BASE}/v1/enquiries`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...form,
          participantCount: form.participantCount || undefined,
          organisation: form.organisation || undefined,
          phone: form.phone || undefined,
          message: form.message || undefined,
          source,
        }),
      });
      if (res.status === 429) throw new Error("Too many requests just now — please try again shortly.");
      if (!res.ok) throw new Error("We could not send that. Check your details and try again.");
      setState("sent");
    } catch (e2) {
      setState("idle");
      setError((e2 as Error).message);
    }
  }

  if (state === "sent") {
    return (
      <div role="status" className="card p-6 text-center">
        <p className="text-lg font-medium text-ink">Thanks — we have your details.</p>
        <p className="mt-2 text-sm text-mut">
          Someone will be in touch within one business day to arrange a walkthrough.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card p-6 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Your name</span>
          <input required value={form.name} onChange={set("name")} autoComplete="name" className="input" />
        </label>
        <label className="block">
          <span className="label">Work email</span>
          <input required type="email" value={form.email} onChange={set("email")}
            autoComplete="email" className="input" />
        </label>
        <label className="block">
          <span className="label">Organisation</span>
          <input value={form.organisation} onChange={set("organisation")}
            autoComplete="organization" className="input" />
        </label>
        <label className="block">
          <span className="label">Phone</span>
          <input value={form.phone} onChange={set("phone")} autoComplete="tel" className="input" />
        </label>
      </div>

      <label className="block">
        <span className="label">How many participants do you support?</span>
        <select value={form.participantCount} onChange={set("participantCount")} className="input">
          <option value="">Prefer not to say</option>
          {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="label">Anything you want us to cover?</span>
        <textarea value={form.message} onChange={set("message")} rows={3} className="input mt-0"
          placeholder="SIL rostering, PACE claiming, migrating off spreadsheets…" />
      </label>

      {/* Honeypot. Hidden from people and from screen readers; bots fill it in. */}
      <div className="hidden" aria-hidden="true">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
        </label>
      </div>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      <button disabled={state === "sending"} className="btn-primary w-full sm:w-auto">
        {state === "sending" ? "Sending…" : "Request a walkthrough"}
      </button>
      <p className="text-xs text-faint">
        We use your details to arrange the demo and nothing else. No newsletter, no reselling.
      </p>
    </form>
  );
}
